// What a refused Control Plane request actually MEANS.
//
// The console used to render every failure through `describeError`, which is
// `e instanceof Error ? e.message : String(e)`. For an `ApiHttpError` that
// produces "HTTP 403 unauthorized from /v2/admin/bff/control-plane/..." — a
// string that tells an operator the request failed and nothing they can act
// on. Four completely different conditions reach that same sentence:
//
//   * the write freeze is closed          → nothing you do in this console helps
//   * you have no approved proposal       → create one, get it approved
//   * your proposal is for something else → create the RIGHT one
//   * you are no longer a vault owner     → your authority was removed
//
// Collapsing those is not a cosmetic problem. An operator told "try again"
// when their proposal was for a different revision will try again, and be
// refused again, and conclude the console is broken.
//
// EVERY BRANCH BELOW IS DERIVED FROM A SERVER RESPONSE, never from elapsed
// time, a local guess, or an optimistic assumption. Where the server declined
// to say which of several things happened, this module says so rather than
// picking the most likely one.
//
// Source of the taxonomy, in `gao-id-worker` at develop `5c340b5`:
//   * `src/middleware/admin-write-gate.ts`      — the write freeze envelope
//   * `src/lib/admin-owner-enforcement.ts`      — `failClose` / `mapReasonToStatus`
//   * `src/lib/admin-multisign-approval.ts`     — `RequireThresholdReason`
//   * `src/lib/control-plane.approval.ts`       — `BindingFailure`
//   * `src/routes/admin-bff-control-plane.ts`   — the per-route conflicts

import { ApiHttpError, RouteNotFoundError } from "../api/client";

export type ControlPlaneFailureKind =
  /** 401 — no session, or the session is no longer accepted. */
  | "not_authenticated"
  /** 403 `gaosafe_approval_required` — the server-side write freeze. */
  | "write_frozen"
  /** 403 at the session stage of a privileged call: live vault membership was
   *  re-read for this request and the caller is no longer an owner. */
  | "authority_lost"
  /** 403 `proposal_required` — this act needs an approved proposal and none
   *  was sent. */
  | "proposal_required"
  | "proposal_unknown"
  /** Approvals collected have not reached the threshold. */
  | "proposal_not_approved"
  | "proposal_expired"
  | "proposal_rejected"
  | "proposal_cancelled"
  /** The proposal is approved, but it authorises a DIFFERENT act. */
  | "proposal_mismatch"
  /** 409 — the proposal was already spent. Not a retry: the effect happened. */
  | "proposal_consumed"
  /** 409 — state moved under the request (revision changed, already current,
   *  already bootstrapped, nothing to publish). */
  | "conflict"
  /** 422 — the document does not validate. */
  | "validation"
  /** 404 carrying the server's own `not_found` envelope. */
  | "not_found"
  /** 404 with no API envelope — the route itself is missing. */
  | "route_missing"
  | "rate_limited"
  | "unavailable"
  | "bad_request"
  /** The request never produced a response. */
  | "network"
  | "unknown";

/** What the operator can usefully DO next. Rendered as the action beside the
 *  message, and deliberately not "try again" for the cases where trying again
 *  is refused identically. */
export type Recovery =
  | "reauthenticate"
  | "reload"
  | "new_proposal"
  | "get_approval"
  | "retry"
  | "none";

export interface ControlPlaneFailure {
  kind: ControlPlaneFailureKind;
  /** Short line for a banner heading. */
  title: string;
  /** One sentence naming what happened and what it means. */
  detail: string;
  /** The server's machine-readable slug when it gave one. */
  reason: string | null;
  status: number | null;
  recovery: Recovery;
}

/** `verifyControlPlaneProposalBinding`'s failure set — an approved proposal
 *  that does not authorise THIS act. */
const BINDING_REASONS: ReadonlySet<string> = new Set([
  "payload_unreadable",
  "payload_tampered",
  "wrong_domain",
  "wrong_operation",
  "wrong_product",
  "wrong_lane",
  "wrong_target",
]);

/** Which dimension of the proposal disagreed, in operator words. */
export const BINDING_REASON_TEXT: Readonly<Record<string, string>> = {
  payload_unreadable: "its stored payload could not be read",
  payload_tampered: "its stored payload no longer matches the digest recorded when it was created",
  wrong_domain: "it was created for a different family of action",
  wrong_operation: "it approves a different operation",
  wrong_product: "it approves this operation on a different product",
  wrong_lane: "it was approved on a different lane, and a lane's approval cannot be spent on another",
  wrong_target: "it approves a different target — the document or revision has moved since it was created",
};

function bodyOf(e: ApiHttpError): Record<string, unknown> {
  return e.body !== null && typeof e.body === "object"
    ? (e.body as Record<string, unknown>)
    : {};
}

function str(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

/**
 * Classify a thrown error from `controlPlane/api.ts`.
 *
 * Never throws, and never returns a kind it cannot justify from the response.
 * An unrecognised shape is `unknown` with the raw message carried through —
 * a wrong confident label is worse than an honest one that says less.
 */
export function classifyControlPlaneError(e: unknown): ControlPlaneFailure {
  if (e instanceof RouteNotFoundError) {
    return {
      kind: "route_missing",
      title: "Route not found",
      detail:
        `${e.path} returned 404 with no API error body. The Control Plane ` +
        `route registration on this tier may have regressed.`,
      reason: null,
      status: 404,
      recovery: "none",
    };
  }

  if (e instanceof ApiHttpError) {
    const body = bodyOf(e);
    const code = str(body.error);
    const message = str(body.message);
    const reason = str(body.reason);
    const detailField = str(body.detail);

    if (e.status === 401) {
      return {
        kind: "not_authenticated",
        title: "Not signed in",
        detail:
          "The server did not accept this session" +
          (reason ? ` (${reason})` : "") +
          ". Sign in again with the owner wallet.",
        reason,
        status: 401,
        recovery: "reauthenticate",
      };
    }

    if (e.status === 403) {
      if (code === "gaosafe_approval_required") {
        return {
          kind: "write_frozen",
          title: "GAOSAFE APPROVAL REQUIRED",
          detail:
            detailField ??
            "Privileged Control Plane mutations are held closed on this tier. " +
              "Nothing in this console can open them; the write gate is server-side.",
          reason,
          status: 403,
          recovery: "none",
        };
      }

      // The executor's own binding refusal. It arrives either with a binding
      // slug in `reason`, or as the executor's message when the proposal row
      // itself could not be read.
      if (reason !== null && BINDING_REASONS.has(reason)) {
        return {
          kind: "proposal_mismatch",
          title: "That approval is for a different action",
          detail:
            `The proposal is approved, but ${BINDING_REASON_TEXT[reason]}. ` +
            `Create a proposal for this exact action and have that one approved.`,
          reason,
          status: 403,
          recovery: "new_proposal",
        };
      }
      if (message === "proposal_unreadable") {
        return {
          kind: "proposal_unknown",
          title: "Proposal could not be read",
          detail:
            "The server could not read that proposal. It may not exist on this " +
            "tier, or the approvals store did not answer.",
          reason,
          status: 403,
          recovery: "new_proposal",
        };
      }

      switch (reason) {
        case "proposal_required":
          return {
            kind: "proposal_required",
            title: "Approval required",
            detail:
              "This action needs an approved GaoSafe proposal and none was sent.",
            reason,
            status: 403,
            recovery: "new_proposal",
          };
        case "proposal_unknown":
          return {
            kind: "proposal_unknown",
            title: "Unknown proposal",
            detail: "The server has no proposal with that id on this tier.",
            reason,
            status: 403,
            recovery: "new_proposal",
          };
        case "proposal_not_approved":
          return {
            kind: "proposal_not_approved",
            title: "Not approved yet",
            detail:
              "The proposal exists but has not reached its approval threshold. " +
              "It has to be approved on the Approvals screen before it can be spent.",
            reason,
            status: 403,
            recovery: "get_approval",
          };
        case "proposal_expired":
          return {
            kind: "proposal_expired",
            title: "Proposal expired",
            detail:
              "The proposal passed its expiry before it was executed. Expired " +
              "proposals are never revived — create a new one.",
            reason,
            status: 403,
            recovery: "new_proposal",
          };
        case "proposal_vetoed":
          return {
            kind: "proposal_rejected",
            title: "Proposal rejected",
            detail:
              "A signer rejected this proposal. A rejected proposal cannot be " +
              "approved afterwards.",
            reason,
            status: 403,
            recovery: "new_proposal",
          };
        case "proposal_cancelled":
          return {
            kind: "proposal_cancelled",
            title: "Proposal cancelled",
            detail: "This proposal was cancelled and can no longer be spent.",
            reason,
            status: 403,
            recovery: "new_proposal",
          };
        default:
          return {
            kind: "authority_lost",
            title: "Not authorised",
            detail:
              (message ?? "The server refused this action.") +
              (reason ? ` (${reason})` : "") +
              " Live vault membership is re-read for every privileged request, " +
              "so an authority removed since sign-in is refused here rather than " +
              "at the next login.",
            reason,
            status: 403,
            recovery: "reauthenticate",
          };
      }
    }

    if (e.status === 409) {
      if (message !== null && message.includes("proposal already executed")) {
        return {
          kind: "proposal_consumed",
          title: "That approval was already used",
          detail:
            "This proposal has already been executed once. It is a single-use " +
            "capability, so this is a replay rather than a retry — the effect it " +
            "authorised has already happened. Check the audit trail before " +
            "creating another.",
          reason,
          status: 409,
          recovery: "reload",
        };
      }
      return {
        kind: "conflict",
        title: "State moved",
        detail:
          (message ?? "The server refused this action as a conflict.") +
          (reason ? ` (${reason})` : "") +
          " Reload to see the current state; repeating the same request would " +
          "be refused identically.",
        reason,
        status: 409,
        recovery: "reload",
      };
    }

    if (e.status === 422) {
      return {
        kind: "validation",
        title: "The document does not validate",
        detail:
          message ??
          "The server refused the document. The issues are listed with the draft.",
        reason,
        status: 422,
        recovery: "none",
      };
    }

    if (e.status === 404) {
      if (code === "not_found") {
        return {
          kind: "not_found",
          title: "Not found",
          detail: message ?? "The server does not have that resource.",
          reason,
          status: 404,
          recovery: "reload",
        };
      }
      return {
        kind: "route_missing",
        title: "Route not found",
        detail:
          `${e.path} returned 404 with no API error body. The Control Plane ` +
          `route registration on this tier may have regressed.`,
        reason,
        status: 404,
        recovery: "none",
      };
    }

    if (e.status === 429) {
      return {
        kind: "rate_limited",
        title: "Too many requests",
        detail:
          message ??
          "The publish rate limit refused this request. Wait a moment and repeat it.",
        reason,
        status: 429,
        recovery: "retry",
      };
    }

    if (e.status === 400) {
      return {
        kind: "bad_request",
        title: "The server refused the request",
        detail: message ?? "The request was not accepted.",
        reason,
        status: 400,
        recovery: "none",
      };
    }

    if (e.status === 503) {
      return {
        kind: "unavailable",
        title: "A required service did not answer",
        detail:
          (message ?? "A dependency of this action is unavailable.") +
          " Nothing was changed — this tier refuses a privileged write it " +
          "cannot audit.",
        reason,
        status: 503,
        recovery: "retry",
      };
    }

    return {
      kind: "unknown",
      title: `HTTP ${e.status}`,
      detail: message ?? e.message,
      reason,
      status: e.status,
      recovery: "retry",
    };
  }

  if (e instanceof TypeError) {
    // `fetch` rejects with a TypeError when the request never reached a
    // server. There is no status and no body, so nothing about the server's
    // state can be inferred — including whether the write landed.
    return {
      kind: "network",
      title: "The request did not reach the server",
      detail:
        `${e.message}. Whether the server acted is unknown from here — reload ` +
        `and read the current state rather than repeating the request.`,
      reason: null,
      status: null,
      recovery: "reload",
    };
  }

  return {
    kind: "unknown",
    title: "Unexpected failure",
    detail: e instanceof Error ? e.message : String(e),
    reason: null,
    status: null,
    recovery: "retry",
  };
}

/** The whole failure as one line, for surfaces that have room for one. */
export function failureLine(f: ControlPlaneFailure): string {
  return `${f.title} — ${f.detail}`;
}

export const RECOVERY_TEXT: Readonly<Record<Recovery, string>> = {
  reauthenticate: "Sign in again",
  reload: "Reload this page",
  new_proposal: "Request a new approval",
  get_approval: "Approve it on the Approvals screen",
  retry: "Repeat the request",
  none: "",
};
